import { startOfCalendarWeek } from '~/calculations/calendar'
import type { ValidationIssues } from '~/models/common'
import { type DomainGoalRecord, isPlanned } from '~/models/fitnessDomain'
import { type ProjectRecord, projectHasStarted } from '~/models/project'
import {
  addOneOffSession,
  buildTrainingWeek,
  countTrainingSessions,
  moveSession,
  type OneOffSessionInput,
  recordSessionOutcome,
  type ScheduledSessionSeed,
  type SessionOutcome,
  type TrainingSessionCounts,
  type TrainingWeekRecord,
} from '~/models/trainingWeek'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import { listParticipatingDomainGoals } from '~/storage/repositories/domainGoalRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import {
  findTrainingWeek,
  materialiseTrainingWeek,
  putTrainingWeek,
} from '~/storage/repositories/trainingWeekRepository'
import { listWeeklyReviewsForWeek } from '~/storage/repositories/weeklyReviewRepository'
import { type CalendarDate, calendarDateFrom, toIsoTimestamp, unwrapDate } from '~/types/dates'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { type DayClosedError, isWeekFinalized, WEEK_CLOSED_MESSAGE } from './dailyCheckService'
import { getEffectiveWeekStartDay } from './weekBoundaryService'

/**
 * The execution layer (D-105 §2, §4, §5, §6, §11).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  NOTHING IN THIS FILE WRITES TO A SCHEDULE, AND THAT IS THE ARCHITECTURE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Complete, move, skip and add-a-one-off touch the materialised week alone.
 *  `domainGoals` is **read** here — to find out what this week should contain —
 *  and is never written. If a change in this file starts needing
 *  `updateDomainGoal`, the change is in the wrong layer: a plan change belongs to
 *  My Plan and takes effect from the start of next week (D-099 Amendment 2).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ONLY THE CURRENT WEEK IS ACTIONABLE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Every action resolves the current week and looks for the session inside it.
 *  A session belonging to a past week is simply not found, which makes "past
 *  weeks are immutable history" structural rather than a rule someone has to
 *  remember — there is no path here that can reach one.
 */

export type TrainingWeekError = DayClosedError | StorageError

export function isWeekClosed(error: TrainingWeekError): error is DayClosedError {
  return error.kind === 'dayClosed'
}

function invalid(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}

/* ── Materialisation ─────────────────────────────────────────────────────── */

/**
 * The current week, materialising it on first read.
 *
 * **`undefined` is a real answer and means there is nothing to execute** — no
 * active project, or no participating domain has a schedule yet. It is
 * deliberately not an empty record: writing one would create a week for every
 * user who has never made a schedule, and a week nobody planned is not a week
 * anybody saw.
 *
 * **Only the current week is ever materialised** (D-105 §6). A future week is
 * never written — a schedule edit would then have to rewrite it, which §2
 * forbids — and a past week is never manufactured, because if the app was not
 * opened all week there was nothing to resolve and nothing was missed.
 */
export async function getCurrentTrainingWeek(
  db: SolaDatabase,
  now: Date,
): Promise<Result<TrainingWeekRecord | undefined, StorageError>> {
  return materialiseCurrentWeek(db, now, false)
}

async function materialiseCurrentWeek(
  db: SolaDatabase,
  now: Date,
  evenWhenEmpty: boolean,
): Promise<Result<TrainingWeekRecord | undefined, StorageError>> {
  const weekStartDay = await getEffectiveWeekStartDay(db, now)
  if (!weekStartDay.ok) return weekStartDay

  const project = await getActiveProject(db)
  if (!project.ok) return project
  if (project.value === undefined) return ok(undefined)

  /* A project that has not begun materialises nothing (D-107 §4).
     The gate is HERE rather than in the screens, because this function writes as
     a side effect of being asked and all four callers reach it — including
     `addOneOffTrainingSession`, whose `evenWhenEmpty` would otherwise create an
     empty week record for a week the project has not reached. That record is
     precisely the week nobody saw that D-105 §6 forbids, manufactured by the app
     itself rather than by a migration. `startedWithin` below is a different rule
     and stays: it trims the days before a mid-week start on the first week that
     IS actionable. */
  if (!projectHasStarted(project.value, calendarDateFrom(now))) return ok(undefined)

  const weekStartDate = startOfCalendarWeek(calendarDateFrom(now), weekStartDay.value)

  /* Read before building. An existing week is returned exactly as stored, so a
     schedule edited on Wednesday changes nothing already on screen — and the
     record's own `weekStartDay` stays authoritative even if the setting has since
     been promoted. */
  const existing = await findTrainingWeek(db, project.value.id, weekStartDate)
  if (!existing.ok) return existing
  if (existing.value !== undefined) return ok(existing.value)

  const goals = await listParticipatingDomainGoals(db, project.value.id)
  if (!goals.ok) return goals

  const planned = goals.value.filter(hasSchedule)
  const sessions = planned.flatMap(seedsFor)
  if (sessions.length === 0 && !evenWhenEmpty) return ok(undefined)

  const built = buildTrainingWeek(
    {
      projectId: project.value.id,
      weekStartDate,
      weekStartDay: weekStartDay.value,
      sourceGoalIds: planned.map((goal) => goal.id),
      sessions,
      ...startedWithin(project.value, weekStartDate),
    },
    toIsoTimestamp(now),
  )
  if (!built.ok) return err(invalid(built.error))

  const written = await materialiseTrainingWeek(db, built.value)
  if (!written.ok) return written
  return ok(written.value.week)
}

/**
 * Whether this goal contributes sessions.
 *
 * **`isPlanned`, not `isParticipating`** — a `trackOnly` domain is observed and
 * never generates work (D-090), and D-105 §7 says plainly that `trackOnly` and
 * inactive domains acquire no sessions. A goal whose schedule is empty
 * contributes nothing either, and is left out of `sourceGoalIds` rather than
 * recorded as a source that produced nothing.
 */
function hasSchedule(goal: DomainGoalRecord): boolean {
  return isPlanned(goal) && (goal.weeklySchedule?.length ?? 0) > 0
}

function seedsFor(goal: DomainGoalRecord): readonly ScheduledSessionSeed[] {
  return (goal.weeklySchedule ?? []).map((session) => ({
    sourceSessionId: session.id,
    domain: goal.domain,
    name: session.name,
    ...(session.expectedMinutes === undefined ? {} : { expectedMinutes: session.expectedMinutes }),
    weekday: session.weekday,
  }))
}

/**
 * The project's start date, when the project began inside this week.
 *
 * Absent for every ordinary week. It exists for the project started on a
 * Wednesday, whose Monday and Tuesday sessions would otherwise materialise
 * already unresolved — work from before the plan existed, arriving as something
 * the user appears to have failed to do on their first day.
 */
function startedWithin(
  project: ProjectRecord,
  weekStartDate: CalendarDate,
): { readonly notBefore?: CalendarDate } {
  return unwrapDate(project.startDate) > unwrapDate(weekStartDate)
    ? { notBefore: project.startDate }
    : {}
}

/**
 * What one week planned and completed, for the review to display (D-105 §3).
 *
 * **Reads only, and never materialises.** A review can cover a week that has
 * already ended, and materialising a past week would manufacture the record
 * D-105 §6 forbids — a week nobody saw, invented so it could be reported as
 * empty.
 *
 * **`undefined` means there is no materialised week for that date**, which is
 * the legacy case: the review keeps its hand-typed fields and its existing
 * interpretation, unchanged and unmigrated. Distinguishing "no record" from
 * "a record with nothing in it" is the whole point of returning `undefined`
 * rather than two zeroes.
 */
export async function getTrainingCountsForWeek(
  db: SolaDatabase,
  weekStartDate: CalendarDate,
): Promise<Result<TrainingSessionCounts | undefined, StorageError>> {
  const project = await getActiveProject(db)
  if (!project.ok) return project
  if (project.value === undefined) return ok(undefined)

  const week = await findTrainingWeek(db, project.value.id, weekStartDate)
  if (!week.ok) return week
  return ok(week.value === undefined ? undefined : countTrainingSessions(week.value))
}

/* ── Acting on a session ─────────────────────────────────────────────────── */

/**
 * Resolve the week, check the boundary, apply a pure change, write it back.
 *
 * The boundary is checked **before** the change is applied, so a refused action
 * leaves the stored week exactly as the finalized review saw it — the same
 * ordering `recordDailyCheck` uses, for the same reason (D-046).
 */
async function changeCurrentWeek(
  db: SolaDatabase,
  now: Date,
  evenWhenEmpty: boolean,
  apply: (week: TrainingWeekRecord) => Result<TrainingWeekRecord, ValidationIssues>,
): Promise<Result<TrainingWeekRecord, TrainingWeekError>> {
  const current = await materialiseCurrentWeek(db, now, evenWhenEmpty)
  if (!current.ok) return current
  if (current.value === undefined) {
    /* Say which absence this is. A waiting project reports the generic message as
       a defect — the week is missing because the project has not begun, which is
       a state the user chose rather than something that went wrong. Read on the
       error path only. */
    const project = await getActiveProject(db)
    if (project.ok && project.value !== undefined) {
      if (!projectHasStarted(project.value, calendarDateFrom(now))) {
        return err(
          storageError(
            'invalidRecord',
            'This project has not started yet, so there is no training week.',
          ),
        )
      }
    }
    return err(storageError('invalidRecord', 'There is no training week to change.'))
  }
  const week = current.value

  const reviews = await listWeeklyReviewsForWeek(db, week.weekStartDate)
  if (!reviews.ok) return reviews
  if (isWeekFinalized(reviews.value)) {
    return err({
      kind: 'dayClosed',
      message: WEEK_CLOSED_MESSAGE,
      weekStartDate: week.weekStartDate,
    })
  }

  const changed = apply(week)
  if (!changed.ok) return err(invalid(changed.error))
  if (changed.value === week) return ok(week)

  return putTrainingWeek(db, changed.value)
}

export async function completeTrainingSession(
  db: SolaDatabase,
  sessionId: string,
  now: Date,
): Promise<Result<TrainingWeekRecord, TrainingWeekError>> {
  const outcome: SessionOutcome = { kind: 'completed', at: toIsoTimestamp(now) }
  return changeCurrentWeek(db, now, false, (week) =>
    recordSessionOutcome(week, sessionId, outcome, toIsoTimestamp(now)),
  )
}

/**
 * Skip a session, explicitly.
 *
 * **An explicit skip is not the same state as an unresolved one** (D-105 §4). The
 * user decided; nothing decided it for them, and nothing ever will — an
 * unresolved session at the week boundary keeps `outcome: undefined` and is never
 * converted into a skip to tidy the record.
 */
export async function skipTrainingSession(
  db: SolaDatabase,
  sessionId: string,
  now: Date,
  reason?: string,
): Promise<Result<TrainingWeekRecord, TrainingWeekError>> {
  const outcome: SessionOutcome = {
    kind: 'skipped',
    at: toIsoTimestamp(now),
    ...(reason === undefined || reason.trim().length === 0 ? {} : { reason: reason.trim() }),
  }
  return changeCurrentWeek(db, now, false, (week) =>
    recordSessionOutcome(week, sessionId, outcome, toIsoTimestamp(now)),
  )
}

/** Move a session to another day this week, leaving its outcome untouched. */
export async function moveTrainingSession(
  db: SolaDatabase,
  sessionId: string,
  to: CalendarDate,
  now: Date,
): Promise<Result<TrainingWeekRecord, TrainingWeekError>> {
  return changeCurrentWeek(db, now, false, (week) =>
    moveSession(week, sessionId, to, toIsoTimestamp(now)),
  )
}

/**
 * Add a session for this week alone.
 *
 * This is the one action that materialises a week that would otherwise be
 * empty — a user with no schedule can still record that they trained today, and
 * refusing them because their plan is empty would make the feature useless to
 * exactly the person it is most likely to help.
 */
export async function addOneOffTrainingSession(
  db: SolaDatabase,
  input: OneOffSessionInput,
  now: Date,
): Promise<Result<TrainingWeekRecord, TrainingWeekError>> {
  return changeCurrentWeek(db, now, true, (week) =>
    addOneOffSession(week, input, toIsoTimestamp(now)),
  )
}
